import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  IconButton,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material";
import {
  CameraAlt as CameraIcon,
  Edit as ManualIcon,
  Close as CloseIcon,
  FlashlightOn as FlashOnIcon,
  FlashlightOff as FlashOffIcon,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { Html5Qrcode } from "html5-qrcode";
import { useTranslation } from "react-i18next";

interface BarcodeScannerProps {
  open: boolean;
  onScan: (code: string, format: string) => void;
  onClose: () => void;
  title?: string;
}

const SCANNER_REGION_ID = "barcode-scanner-region";

const BarcodeScanner: React.FC<BarcodeScannerProps> = ({
  open,
  onScan,
  onClose,
  title,
}) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [manualCode, setManualCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scanSuccess, setScanSuccess] = useState(false);
  const [lastScannedCode, setLastScannedCode] = useState<string | null>(null);
  const [torchOn, setTorchOn] = useState(false);

  const scannerRef = useRef<Html5Qrcode | null>(null);
  const mountedRef = useRef(true);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try {
        const state = scannerRef.current.getState();
        if (state === 2) {
          await scannerRef.current.stop();
        }
      } catch {
        // ignore
      }
      try {
        scannerRef.current.clear();
      } catch {
        // ignore
      }
      scannerRef.current = null;
    }
  }, []);

  const startScanner = useCallback(async () => {
    await stopScanner();

    const el = document.getElementById(SCANNER_REGION_ID);
    if (!el) return;

    setError(null);
    setScanning(true);
    setScanSuccess(false);
    setLastScannedCode(null);

    try {
      const scanner = new Html5Qrcode(SCANNER_REGION_ID, false);
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: "environment" },
        {
          fps: 15,
          qrbox: { width: 300, height: 150 },
          aspectRatio: 1.5,
        },
        (decodedText, result) => {
          if (!mountedRef.current) return;
          const format = result?.result?.format?.formatName || "UNKNOWN";
          setLastScannedCode(decodedText);
          setScanSuccess(true);

          if (navigator.vibrate) {
            navigator.vibrate(100);
          }

          setTimeout(() => {
            if (mountedRef.current) {
              onScan(decodedText, format);
            }
          }, 600);
        },
        () => {
          // QR scan failure callback (called per frame when nothing found) — no-op
        }
      );
    } catch (err: any) {
      if (mountedRef.current) {
        setError(
          err?.message || t("barcodes.cameraError", "Nie udało się uruchomić kamery")
        );
        setScanning(false);
      }
    }
  }, [onScan, stopScanner, t]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (open && tab === 0) {
      const timeout = setTimeout(() => {
        startScanner();
      }, 400);
      return () => {
        clearTimeout(timeout);
        stopScanner();
      };
    } else {
      stopScanner();
    }
  }, [open, tab, startScanner, stopScanner]);

  useEffect(() => {
    if (!open) {
      setManualCode("");
      setError(null);
      setScanSuccess(false);
      setLastScannedCode(null);
      setTab(0);
      setTorchOn(false);
    }
  }, [open]);

  const handleTorchToggle = async () => {
    if (!scannerRef.current) return;
    try {
      const state = scannerRef.current.getState();
      if (state !== 2) return;
      if (torchOn) {
        await (scannerRef.current as any).applyVideoConstraints({ advanced: [{ torch: false }] });
      } else {
        await (scannerRef.current as any).applyVideoConstraints({ advanced: [{ torch: true }] });
      }
      setTorchOn(!torchOn);
    } catch {
      // torch not supported
    }
  };

  const handleManualSubmit = () => {
    const code = manualCode.trim();
    if (!code) return;
    onScan(code, "MANUAL");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleManualSubmit();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#1e1e1e",
          backgroundImage: "none",
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {title || t("barcodes.scan", "Skanuj kod kreskowy")}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "text.secondary" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 1 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ mb: 2 }}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab icon={<CameraIcon />} label={t("barcodes.camera", "Kamera")} />
          <Tab icon={<ManualIcon />} label={t("barcodes.manual", "Ręcznie")} />
        </Tabs>

        {tab === 0 && (
          <Box>
            <AnimatePresence mode="wait">
              {scanSuccess ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: 250,
                      bgcolor: "rgba(0, 169, 140, 0.15)",
                      borderRadius: 2,
                      border: "2px solid",
                      borderColor: "primary.main",
                    }}
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: "50%",
                          bgcolor: "primary.main",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 2,
                        }}
                      >
                        <Typography variant="h4" color="white">
                          ✓
                        </Typography>
                      </Box>
                    </motion.div>
                    <Typography variant="body1" fontWeight={600}>
                      {t("barcodes.scanSuccess", "Zeskanowano!")}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5, fontFamily: "monospace" }}
                    >
                      {lastScannedCode}
                    </Typography>
                  </Box>
                </motion.div>
              ) : (
                <motion.div
                  key="scanner"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      minHeight: 250,
                      bgcolor: "#000",
                      borderRadius: 2,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      id={SCANNER_REGION_ID}
                      sx={{
                        width: "100%",
                        minHeight: 250,
                        "& video": {
                          borderRadius: 2,
                        },
                      }}
                    />

                    {scanning && (
                      <IconButton
                        onClick={handleTorchToggle}
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          bgcolor: "rgba(0,0,0,0.5)",
                          color: torchOn ? "primary.main" : "white",
                          "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                        }}
                        size="small"
                      >
                        {torchOn ? <FlashOnIcon /> : <FlashOffIcon />}
                      </IconButton>
                    )}

                    {!scanning && !error && (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CircularProgress size={32} />
                      </Box>
                    )}
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>

            {error && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1.5, textAlign: "center" }}
            >
              {t(
                "barcodes.scanHint",
                "Skieruj kamerę na kod kreskowy. Obsługiwane: EAN-13, EAN-8, UPC-A, Code128, Code39, QR"
              )}
            </Typography>
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label={t("barcodes.enterCode", "Wpisz kod kreskowy")}
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
              variant="outlined"
              placeholder="np. 841308125220"
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              fullWidth
              onClick={handleManualSubmit}
              disabled={!manualCode.trim()}
              size="large"
            >
              {t("barcodes.confirm", "Zatwierdź")}
            </Button>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          {t("common.cancel", "Anuluj")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BarcodeScanner;
